/**
 * CrossTabDragCoach — announces the ⌥Alt+drag cross-window copy gesture at
 * the exact moment of intent (docs/cross-tab-drag-plan.md, P3).
 *
 * A pointer drag physically cannot leave the window: the instant a user
 * drags an item toward another window is both the moment of intent and the
 * moment of silent failure — the tile just stops at the edge. When a LIVE
 * pointer cross-grid drag brings the pointer within EDGE_PX of the viewport
 * edge, surface a coach pill teaching the NEXT gesture (a live pointer drag
 * can NOT convert into a native drag mid-flight, so the wording promises no
 * hand-off). Shown at most once per drag, capped across sessions
 * (localStorage, MAX_IMPRESSIONS) — after that the user knows the trick.
 *
 * Subscribes to the same `interaction.crossGrid.pointer` stream the ghost
 * already rides — no new listeners on the hot path.
 */
import { useEffect, useRef, useState } from "react";
import { useGridStore } from "@fredericrous/grid";
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { spacing, radii } from "@duro-app/tokens/tokens/spacing.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import { typeScale } from "@duro-app/tokens/tokens/typography.css";
import { layers } from "@duro-app/tokens/tokens/layers.css";

const SEEN_KEY = "wb-crosstab-tip-v1";
const MAX_IMPRESSIONS = 3;
const EDGE_PX = 24;

function impressions(): number {
  try {
    return Number(localStorage.getItem(SEEN_KEY) ?? "0") || 0;
  } catch {
    return 0;
  }
}

function recordImpression(): void {
  try {
    localStorage.setItem(SEEN_KEY, String(impressions() + 1));
  } catch {
    // private mode — session-only is fine
  }
}

export function CrossTabDragCoach(): React.ReactElement | null {
  const [visible, setVisible] = useState(false);
  // Once per DRAG: reset when the gesture ends.
  const shownThisDrag = useRef(false);

  useEffect(() => {
    if (impressions() >= MAX_IMPRESSIONS) return;
    return useGridStore.subscribe((s) => {
      const cg = s.interaction.crossGrid;
      if (!cg) {
        shownThisDrag.current = false;
        // Keep the pill up briefly after release so it's readable — it
        // self-dismisses below.
        return;
      }
      if (shownThisDrag.current) return;
      const { x, y } = cg.pointer;
      const nearEdge =
        x <= EDGE_PX ||
        y <= EDGE_PX ||
        x >= window.innerWidth - EDGE_PX ||
        y >= window.innerHeight - EDGE_PX;
      if (!nearEdge) return;
      shownThisDrag.current = true;
      recordImpression();
      setVisible(true);
    });
  }, []);

  // Auto-dismiss after a readable interval.
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => setVisible(false), 6000);
    return () => clearTimeout(t);
  }, [visible]);

  if (!visible) return null;
  return (
    <html.div role="status" aria-live="polite" style={s.pill}>
      <html.span>
        💡 To copy into another window: <html.strong>release</html.strong>, then
        hold <html.strong>⌥</html.strong> and drag
      </html.span>
    </html.div>
  );
}

const s = css.create({
  pill: {
    position: "absolute",
    // 72px: no spacing token within ±10% (xxxl is 64) — the authored offset.
    bottom: 72,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: layers.floating,
    display: "flex",
    alignItems: "center",
    maxWidth: `calc(100% - ${spacing.xl})`,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
    paddingLeft: spacing.md,
    paddingRight: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.contrastSurface,
    color: colors.onContrastSurface,
    fontSize: typeScale.fontSize2,
    lineHeight: 1.3,
    boxShadow: shadows.lg,
    pointerEvents: "none",
  },
});
